"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import { PROFILE_HISTORY_EDITOR_SCHEMAS } from "../profileCards.constants";
import messages from "@/i18n/messages/en.json";

import { useId, useMemo, useState } from "react";

import {
  Button,
  DropdownSelect,
  LocationInput,
  MonthPicker,
  toMonthValue,
} from "@artha/ui";
import { createGooglePlacesProvider } from "../../../../common/services/googlePlaces";

import {
  PROFILE_CARD_INPUT_CLASS_NAME,
  PROFILE_CARD_LABEL_CLASS_NAME,
  PROFILE_CARD_REQUIRED_PATTERN,
  PROFILE_EMPLOYMENT_TYPES,
  PROFILE_DEFAULT_EMPLOYMENT_TYPE,
} from "../profileCards.constants";
import { profileEmploymentType } from "../profileCards.utils";
import type {
  ProfileHistoryEditorProps,
  ProfileHistoryEntry,
} from "../profileCards.types";

export function ProfileHistoryEditor({
  entry,
  config,
  isNew,
  onSave,
  onCancel,
  onRemove,
}: ProfileHistoryEditorProps) {
  const [draft, setDraft] = useState(() => ({
    ...entry,
    ...(config.singular === "experience"
      ? {
          employmentType: profileEmploymentType(entry.employmentType),
        }
      : {}),
  }));
  const [dateError, setDateError] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const locationProvider = useMemo(() => createGooglePlacesProvider(), []);
  const id = useId();
  return (
    <form
      className="space-y-3"
      aria-label={talentNetworkText(isNew ? "add_entry" : "edit_entry", {
        entry: config.singular,
      })}
      onSubmit={(event) => {
        event.preventDefault();
        if (isResolvingLocation) return;
        const start = toMonthValue(draft.startDate);
        const end = toMonthValue(draft.endDate);
        if (!draft.isCurrent && start && end && start > end) {
          setDateError(true);
          return;
        }
        const clean = Object.fromEntries(
          Object.entries(draft)
            .map(([key, value]) => [
              key,
              typeof value === "string" ? value.trim() : value,
            ])
            .filter(([, value]) => value !== "" && value !== undefined),
        ) as ProfileHistoryEntry;
        if (clean.isCurrent) delete clean.endDate;
        const parsed =
          PROFILE_HISTORY_EDITOR_SCHEMAS[config.singular].safeParse(clean);
        if (!parsed.success) {
          setValidationError(parsed.error.issues[0].message);
          return;
        }
        onSave(parsed.data);
      }}
    >
      {config.fields.map((field) =>
        field.key === "location" ? (
          <LocationInput
            key={field.key}
            mode="single"
            label={field.label}
            maxLength={field.maxLength}
            value={draft.location || ""}
            provider={locationProvider}
            onChange={(location) =>
              setDraft((current) => ({ ...current, location }))
            }
            onResolvingChange={setIsResolvingLocation}
            labelClassName={PROFILE_CARD_LABEL_CLASS_NAME}
            controlClassName={PROFILE_CARD_INPUT_CLASS_NAME}
          />
        ) : (
          <div key={field.key}>
            <label
              className={PROFILE_CARD_LABEL_CLASS_NAME}
              htmlFor={`${id}-${field.key}`}
            >
              {field.label}
              {field.required ? " *" : ""}
            </label>
            {field.key === "employmentType" ? (
              <DropdownSelect
                id={`${id}-${field.key}`}
                ariaLabel={field.label}
                value={draft.employmentType || PROFILE_DEFAULT_EMPLOYMENT_TYPE}
                options={[
                  ...new Set<string>([
                    ...PROFILE_EMPLOYMENT_TYPES,
                    ...(draft.employmentType ? [draft.employmentType] : []),
                  ]),
                ].map((value) => ({ value, label: value }))}
                className={PROFILE_CARD_INPUT_CLASS_NAME}
                onValueChange={(employmentType) =>
                  setDraft({ ...draft, employmentType })
                }
              />
            ) : field.multiline ? (
              <textarea
                id={`${id}-${field.key}`}
                aria-label={field.label}
                rows={4}
                maxLength={field.maxLength}
                className={`${PROFILE_CARD_INPUT_CLASS_NAME} h-auto py-2`}
                value={String(draft[field.key] || "")}
                onChange={(event) =>
                  setDraft({ ...draft, [field.key]: event.target.value })
                }
              />
            ) : field.inputType === "month" ? (
              <MonthPicker
                id={`${id}-${field.key}`}
                label={field.label}
                value={draft[field.key] || ""}
                min={field.key === "endDate" ? draft.startDate : undefined}
                max={
                  field.key === "startDate" && !draft.isCurrent
                    ? draft.endDate
                    : undefined
                }
                disabled={field.key === "endDate" && draft.isCurrent}
                className={PROFILE_CARD_INPUT_CLASS_NAME}
                onChange={(value) => {
                  setDateError(false);
                  setDraft({ ...draft, [field.key]: value });
                }}
              />
            ) : (
              <input
                id={`${id}-${field.key}`}
                aria-label={field.label}
                type={field.key === "url" ? "url" : "text"}
                pattern={
                  field.key === "url"
                    ? "https?://.*"
                    : field.required
                      ? PROFILE_CARD_REQUIRED_PATTERN
                      : undefined
                }
                placeholder={field.placeholder}
                required={field.required}
                maxLength={field.maxLength}
                className={PROFILE_CARD_INPUT_CLASS_NAME}
                value={String(draft[field.key] || "")}
                onChange={(event) =>
                  setDraft({ ...draft, [field.key]: event.target.value })
                }
              />
            )}
            {field.key === "endDate" ? (
              <label className="mt-2 flex items-start gap-2 text-xs text-[#475467]">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={draft.isCurrent === true}
                  onChange={(event) => {
                    setDateError(false);
                    setDraft({ ...draft, isCurrent: event.target.checked });
                  }}
                />
                {config.currentLabel}
              </label>
            ) : null}
          </div>
        ),
      )}
      {validationError ? (
        <p role="alert" className="text-sm text-[#B42318]">
          {validationError}
        </p>
      ) : null}
      {dateError ? (
        <p role="alert" className="text-sm text-[#B42318]">
          {messages.talentNetworkUi.end_date_must_be_the_same_month_as_or}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button
          type="submit"
          disabled={isResolvingLocation}
          className="h-9 rounded-lg px-4 text-sm"
        >
          {messages.talentNetworkUi.save}
          {config.singular}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9 rounded-lg px-4 text-sm"
          onClick={onCancel}
        >
          {messages.talentNetworkUi.cancel}
        </Button>
        {!isNew ? (
          <button
            type="button"
            className="ml-auto text-xs font-semibold text-[#B42318]"
            onClick={onRemove}
          >
            {messages.talentNetworkUi.remove}
            {config.singular}
          </button>
        ) : null}
      </div>
    </form>
  );
}
